/**
 *
 * VideoPlayer
 *
 */

import React from 'react';
import PropTypes from 'prop-types';

import throttle from 'helpers/throttle';
import cloneDeep from 'helpers/cloneDeep';
import iOsDetect from 'helpers/iOsDetect';

import Loader from 'components/Loader';

import VideoPlayerControls from './VideoPlayerControls';
import VideoObserver from './VideoObserver';

import VideoPlayerWrapper from './styles/VideoPlayerWrapper';
import VideoPlayerHolder from './styles/VideoPlayerHolder';
import VideoPlayerStreamOffline from './styles/VideoPlayerStreamOffline';
import randomIdGenerator from 'helpers/randomIdGenerator';


const defaultVideo = {
  src: 'https://acara-assets-public.nyc3.cdn.digitaloceanspaces.com/videoPlaceholder.mp4'
};
const initialState = {
  canPlay: null,
  currentTime: 0,
  duration: null,
  isFullscreen: false,
  isMouseOver: false,
  paused: true,
  pausedTime: 0,
  videoLoaded: false,
  videoSources: null,
  sourceString: '',
  sourceMuxString: '',
  hlsVideo: '',
  url: '',
  isStreamLive: true,
  firstPlayPressed: false,
  videoEnded: false,
  mostViewedData: [],
  currentViewedValue: 0,
  currentViewedTime: 0,
  totalTimeCoverage: 0,
  replayCounter: 0,
  timeSpentWatching: 0
  // coveredRanges: [],
    // errors: [],
};



class VideoPlayer extends React.Component {
  constructor(props) {
    super();

    // if (!window?.videoViewId) {
    //   window.videoViewId =  window.pageSessionId || randomIdGenerator();
    // }

    this._isMounted = false;
    this.isIos = iOsDetect();
    this.streamDurationInterval;
    this.videoSendingInterval;
    this.videoPausedInterval;

    this.autoPlay = this.autoPlay.bind(this);
    this.handleChangeProgress = this.handleChangeProgress.bind(this);
    this.handleReadyStateChange = this.handleReadyStateChange.bind(this);
    this.handleLoadedData = this.handleLoadedData.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleVolumeChange = this.handleVolumeChange.bind(this);
    this.pause = this.pause.bind(this);
    this.play = this.play.bind(this);
    this.setDuration = this.setDuration.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.checkIfStreaming = this.checkIfStreaming.bind(this);
    this.handleFullScreen = this.handleFullScreen.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handlePictureInPicture = this.handlePictureInPicture.bind(this);
    this.handleLeavePictureInPicture = this.handleLeavePictureInPicture.bind(this);
    this.loadMux = this.loadMux.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.sendVideoData = this.sendVideoData.bind(this);
    this.startAnalytics = this.startAnalytics.bind(this);

    this.state = {
      ...cloneDeep(initialState),
      muted: props.muted,
      videoViewId: randomIdGenerator()
    };

    this.playPromise = null;
  }


  startAnalytics() {
    if (!this._isMounted) return;

    const {
      firstPlayPressed,
      videoEnded
    } = this.state;

    const {
      analytics
    } = this.props;


    if (analytics) {

      this.sendVideoData(firstPlayPressed ? false : true);
      this.videoSendingInterval = setInterval(() => {
        this.sendVideoData();
      }, 10000);

      if (videoEnded) {
        this.setState((prevState) => {
          return {
            replayCounter: prevState.replayCounter + 1
          }
        })
      }

      clearInterval(this.videoPausedInterval);
    }



    this.setState({
      firstPlayPressed: true
    });

  }

  handleLeavePictureInPicture() {
    if (!this.video.paused) {
      setTimeout(() => {
        this.play();
      });
    }
  }


  handlePictureInPicture() {
    if (this.video && this._isMounted)  {
      if (document.pictureInPictureElement) document.exitPictureInPicture();
      if (this.video.requestPictureInPicture) this.video.requestPictureInPicture();  
    }
  }

  setVideoFallback() {
    const { sources, sourcesFallback } = this.props;

    const videoSource = sources ? sources : defaultVideo;
    const cloneInitialState = cloneDeep(initialState);
    this.setState({
      ...cloneInitialState,
      muted: this.props.muted,
      videoSources: sourcesFallback && sourcesFallback.src ? (
        <>
          <source src={videoSource.src} type={videoSource.src.includes('webm') ? 'video/webm' : 'video/mp4'} />
          <source src={sourcesFallback.src} type={sourcesFallback.src.includes('webm') ? 'video/webm' : 'video/mp4'} />
        </>
      ) : (
        <source src={videoSource.src} type={videoSource.src.includes('webm') ? 'video/webm' : 'video/mp4'} />
      ),
      sourceString: videoSource.src
    });
  }

  checkDurationForLiveStream() {
    this.streamDurationInterval = setInterval(() => {
      this.setDuration();
    }, 1000);

  }

  checkIfStreaming = throttle(() => {
      if (this._isMounted) {
        const {
          hlsVideo,
          url
        } = this.state;

        if (hlsVideo && url) {
          hlsVideo.loadSource(url);
        }        
      }  
  }, 1000)


  async loadMux() {
    const { sources, isLive } = this.props;

    const url = `https://stream.mux.com/${sources.muxPlaybackId}.m3u8?max_resolution=720p`;
    const importHls = await import(/* webpackMode: "lazy" */ 'hls.js');

    const Hls = importHls.default;

    if (Hls.isSupported()) {
      const hlsVideo = new Hls({capLevelToPlayerSize: true});
      hlsVideo.loadSource(url);
      hlsVideo.attachMedia(this.video);

      this.setState({
        hlsVideo: hlsVideo,
        url: url,
        Hls: Hls
      });
      


      if (isLive) this.checkDurationForLiveStream();
    
      hlsVideo.on(Hls.Events.ERROR, (event, data) => {
        const errorDetails = data.details;
        if (errorDetails === 'manifestLoadError' || data.mediaError === 'mediaError') {
          if (!this._isMounted) return;
          this.setState({
            isStreamLive: false
          });
          window.setTimeout(this.checkIfStreaming, 5000)     
        }
      });

      hlsVideo.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        if (!this._isMounted) return;
        this.setState({
          isStreamLive: true
        });
      });

    }
  }

  async setMuxVideo() {

    if (!this._isMounted) return;

    const { sources, autoplay } = this.props;

    this.setState({
      sourceMuxString: sources.muxPlaybackId,
      canPlay: true,
      videoLoaded: autoplay ? true : 'hls'
    }, async() => {
      if (autoplay) this.loadMux();
    });

  }

  autoPlay() {

    if (!this._isMounted) return;

    const {
      autoplay
    } = this.props;

    const { paused } = this.state;

    if (!paused) return;

    const tryAutoplay = () => {
      if (this && this.video && this.video.paused) {

        const playPromise = this.video.play();   
        if (playPromise !== undefined) {
          playPromise.then(() => {
            if (!this._isMounted) return;
            this.startAnalytics();
            this.setState({ paused: false }, () => {
              try {
                this.handleVolumeChange(1);
              } catch(error) {
                console.log(error);
                this.setState({ paused: true });                
              }
            });
          }).catch((error) => {
            console.log(error);
            setTimeout(tryAutoplay, 1000);
          });
        }
      }      
    }
    if (autoplay) tryAutoplay();   
    
  }

  pause() {

    const {
      analytics
    } = this.props;


    if (!this._isMounted) return;

    if (this.playPromise) {
      this.playPromise.then(() => this.video.pause());
    } else {
      this.video.pause();
    }

    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);

    this.setState({ paused: true });

    if (analytics) {
      this.videoPausedInterval = setInterval(() => {
        this.setState((prevState) => { return { pausedTime: prevState.pausedTime + 1 } });
      }, 1000);
    }
    
  }

  play() {

    if (!this._isMounted) return;

    if (this.video.paused) {
      this.playPromise = this.video.play();

      const {
        duration,
        videoLoaded,
        firstPlayPressed,
        videoEnded
      } = this.state;

      const {
        isLive,
        analytics
      } = this.props;

      if (isLive && duration) {
        const currentDuration = duration / 1000;
        this.video.currentTime = currentDuration > 4 ? currentDuration - 4 : currentDuration;
      }

      if (videoLoaded === 'hls') {
        this.loadMux();
      }

      this.startAnalytics();



      this.setState({
        paused: false,
        videoLoaded: true
      }, () => {
        if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
        this.mouseTimeout = setTimeout(() => {
          this.setState({
            isMouseOver: false
          });
        }, 3000);
      });
    }
  }

  handleChangeProgress(elapsed) {
    if (!this._isMounted || !elapsed || typeof elapsed !== 'number') return;
    this.setState({ currentTime: elapsed });
    this.video.currentTime = elapsed / 1000;
  }

  setDuration(setInitialMostViewedData) {

    if (!this._isMounted) return;
    if (!this.video) return;

    const { duration } = this.video;

    if (setInitialMostViewedData) return this.setState(() => {
      const timeRange = this.video.seekable;
      if (timeRange.length === 0) return;
      const start = timeRange.start(0);
      const end = Math.floor(timeRange.end(0));
  
      const length = Number.isInteger(end) && Number.isInteger(start) ? end - start : Math.floor(this.video.duration);
      const mostViewedData = new Array(length).fill(0)

      return { 
        duration: this.video.duration * 1000,
        mostViewedData: mostViewedData
      }
    });

    this.setState({ duration: duration * 1000 });
  
  }

  handleReadyStateChange(e, type) {
    const { readyState } = this.video;

    const canPlay = readyState > 2;

    if (this.state.canPlay !== canPlay) {
      this.setState({ canPlay });
    }

    // if (this.props.analytics && (['stalled', 'suspend'].includes(type))) {
    //   this.handleError(e, type);
    // }
  }

  handleLoadedData() {
    if (!this._isMounted) return;
    this.setState({ videoLoaded: true });
    this.autoPlay();
  }

  handleTimeUpdate = throttle(() => {
    if (!this._isMounted) return;
    if (this.video && this.video.currentTime) {
      const { currentTime } = this.video;
      const elapsed = currentTime * 1000;
  
      this.setState({ currentTime: elapsed });
    }

    if (this.props.analytics) {

      if (this.video && !this.video.paused) {
        const {
          mostViewedData,
          currentViewedTime,
          currentViewedValue
        } = this.state;

        const newMostViewedData = [...mostViewedData];
        let newCurrentTime = currentViewedTime;
        let newCurrentValue = currentViewedValue;

        if (Math.floor(currentViewedTime) !== Math.floor(this.video.currentTime)) {
          newCurrentTime = Math.floor(this.video.currentTime);
          if (mostViewedData[newCurrentTime - 1] || mostViewedData[newCurrentTime - 1] === 0) {
            newCurrentValue = mostViewedData[newCurrentTime - 1];
            if (!newCurrentValue) {
              newCurrentValue = 0;
            }
            newMostViewedData[newCurrentTime - 1] = mostViewedData[newCurrentTime - 1] + 1
          }
        }

        let timeSpentWatching = newMostViewedData.reduce((partialSum, a) => partialSum + a, 0);
        let totalTimeCoverage = 0;
        // let coveredRanges = [];

        for (let i = 0; i < this.video.played.length; i++) {
          const timeRangeDiff = this.video.played.end(i) - this.video.played.start(i);
          totalTimeCoverage += timeRangeDiff;
          // coveredRanges.push([this.video.played.start(i), this.video.played.end(i)])
        }

        this.setState({
          mostViewedData: newMostViewedData,
          currentViewedTime: newCurrentTime,
          currentViewedValue: newCurrentValue,
          totalTimeCoverage: Math.floor(totalTimeCoverage),
          timeSpentWatching: timeSpentWatching
          // coveredRanges: coveredRanges,
        })
      }

    }

  }, 200);

  // Mouse

  handleMouseMove = throttle(() => {

    if (!this._isMounted) return;


      if (this.mouseTimeout) clearTimeout(this.mouseTimeout);

      if (!this.state.paused && !this.state.mouseOutside) this.setState({ isMouseOver: true }, () => {

        this.mouseTimeout = setTimeout(() => {  
          if (this._isMounted) this.setState({
            isMouseOver: false
          });
        }, 1500);

      });

  }, 300);


  handleMouseEnter() {
    if (!this._isMounted) return;
    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
    this.setState({ isMouseOver: true, mouseOutside: false });
  }

  handleMouseLeave() {
    if (!this._isMounted) return;
    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
    this.setState({ isMouseOver: false, mouseOutside: true });
  }

  // Volume

  handleVolumeChange(volume) {
    if (!this._isMounted) return;
    try {
      this.setState({
        volume: volume,
        muted: volume <= 0
      }, () => {
        if (this.video) this.video.volume = volume;
      })
    } catch(error) {
      console.log(error)
    }
  }

  handleFullScreen(event) {

    if (!this._isMounted) return;
    
    if (!document.fullscreenElement) this.setState({ isFullscreen: false }, () => {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    });

  }


  toggleFullscreen() {

    if (!this._isMounted) return;

    const { isFullscreen } = this.state;

    if (!this.videoContainer) return;

    if (isFullscreen) {
      this.setState({ isFullscreen: false }, () => {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      });
    } else {
      this.setState({ isFullscreen: true }, () => {
        if (this.videoContainer.requestFullscreen) return this.videoContainer.requestFullscreen();
        if (this.videoContainer.webkitRequestFullScreen) return this.videoContainer.webkitRequestFullScreen();
        if (this.videoContainer.webkitEnterFullScreen) return this.videoContainer.webkitEnterFullScreen();
      });
    }
  }


  handleEnded() {
    this.setState({
      videoEnded: true
    })
  }
 


  async sendVideoData(initial) {

    if (this.state.videoEnded) return // what about paused video?

    const deployEnv = process.env.ACARA_DEPLOY_ENV;
    const protocol = deployEnv === 'production' ?  'https://analytics.acara.io' : deployEnv === 'staging' ? 'https://staginganalytics.acara.io' : 'http://localhost:3000';
    const guessTimezoneAnalytics = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isSamePage = document.referrer && window.location.origin == new URL(document.referrer).origin;
    let isExternalReferrer = isSamePage ? null : document.referrer ? document.referrer : null;

    const {
      paused,
      pausedTime,
      mostViewedData,
      totalTimeCoverage,
      replayCounter,
      timeSpentWatching,
      videoViewId,
      duration
    } = this.state;

    const {
      videoAnalyticsData,
      isLive
    } = this.props;

    const thisPayload = {
      paused,
      pausedTime,
      mostViewedData,
      totalTimeCoverage,
      replayCounter,
      timeSpentWatching,
      videoViewId,
      duration,
      userId: videoAnalyticsData && videoAnalyticsData.userId ? videoAnalyticsData.userId : null,
      referrer: isExternalReferrer,
      timezone: guessTimezoneAnalytics,
      location: window.location.href.split('?')[0],
      domain: window.location.hostname,
      pathName: window.location.pathname,
      videoId: videoAnalyticsData && videoAnalyticsData.videoId && !isLive ? videoAnalyticsData.videoId : null,
      streamId: videoAnalyticsData && videoAnalyticsData.videoId && isLive ? videoAnalyticsData.videoId : null,
      organizationId: videoAnalyticsData ? videoAnalyticsData.organizationId : null,
      templateId: videoAnalyticsData ? videoAnalyticsData.templateId : null
    };

      return fetch(protocol+'/video-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
          isInitial: initial ? true: false,
          content: thisPayload
        }),
        keepalive: true
      }).then(response => {
        return response.json().then(responseData => {
          return;
        });
      }).catch(function (err) {
        console.log(err);
      });

  }


  componentDidMount() {
    this._isMounted = true;
    const { sources, forceRawSource, isBackground, analytics } = this.props;

    if (this.video) {
      this.video.addEventListener('leavepictureinpicture', this.handleLeavePictureInPicture);
    }

    if (sources && sources.muxPlaybackId && !this.isIos && !forceRawSource || sources && sources.muxPlaybackId && this.isIos) {
      return this.setMuxVideo();
    }

    if (!this.isIos) {
      this.setVideoFallback();
    }

    if (!isBackground) window.addEventListener('fullscreenchange', this.handleFullScreen);

  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.streamDurationInterval) clearInterval(this.streamDurationInterval);
    if (this.videoSendingInterval) clearInterval(this.videoSendingInterval);
    if (this.videoPausedInterval) clearInterval(this.videoPausedInterval);
    if (!this.props.isBackground) window.removeEventListener('fullscreenchange', this.handleFullScreen);

    if (this.video) {
      this.video.removeEventListener('leavepictureinpicture', this.handleLeavePictureInPicture);
    }
  }


  render() {
    // eslint-disable-next-line no-unused-vars
    const {
      autoplay,
      controls,
      isBackground,
      isLive,
      isResponsive,
      loop,
      sources,
      thisStyles,
      forceRawSource,
      backgroundColor,
      hideLoader,
      thumbnailTimestamp,
      sourcesFallback,
      analytics,
      videoAnalyticsData
    } = this.props;

    const {
      canPlay,
      currentTime,
      duration,
      isFullscreen,
      isMouseOver,
      paused,
      videoLoaded,
      videoSources,
      volume,
      isStreamLive,
      muted,
    } = this.state;

    if (this.isIos) {

      let videoSourceIos = sources ? sources : defaultVideo;

      if (videoSourceIos && videoSourceIos.muxPlaybackId) {
        videoSourceIos = `https://stream.mux.com/${videoSourceIos.muxPlaybackId}.m3u8?max_resolution=720p`;
      } else if (videoSourceIos) {
        videoSourceIos = videoSourceIos.src;
      }

      if (!videoSourceIos) return '';
      
      return  (
        <VideoPlayerWrapper
          $isBackground={isBackground}
          onMouseEnter={this.handleMouseEnter}
          onMouseMove={this.handleMouseMove}
          onTouchMove={this.handleMouseMove}
          onMouseLeave={this.handleMouseLeave}
          ref={node => (this.videoContainer = node)}
          $thisStyles={thisStyles}
          $backgroundColor={backgroundColor}
        >

          <VideoPlayerHolder
            controls={isBackground ? false : true}
            isBackground={isBackground}
            isResponsive={isResponsive}
            muted={muted || isBackground}
            loop={loop}
            autoPlay={isBackground}
            onLoadedMetadata={() => this.setDuration(analytics ? true : false)}
            onCanPlay={this.handleReadyStateChange}
            onCanPlayThrough={this.handleReadyStateChange}
            onStalled={(e) => this.handleReadyStateChange(e, 'stalled')}
            onSuspend={(e) => this.handleReadyStateChange(e, 'suspend')}
            onWaiting={this.handleReadyStateChange}
            onLoadedData={this.handleLoadedData}
            onTimeUpdate={this.handleTimeUpdate}
            onEnded={this.handleEnded}
            // onError={(e) => this.handleError(e, 'error')}
            // onAbort={(e) => this.handleError(e, 'abort')}
            ref={node => {
              this.video = node;
            }}
            playsInline
            poster={sources?.muxPlaybackId && !isBackground ? `https://image.mux.com/${sources?.muxPlaybackId}/thumbnail.jpg?fit_mode=smartcrop&time=${thumbnailTimestamp ? thumbnailTimestamp : 1}` : ''}
          >
            <source src={videoSourceIos} type={videoSourceIos.includes('webm') ? 'video/webm' : 'video/mp4'} />
          </VideoPlayerHolder>
        </VideoPlayerWrapper>
      );
    }

    return (
      <VideoPlayerWrapper
        $isBackground={isBackground}
        onMouseEnter={this.handleMouseEnter}
        onMouseMove={this.handleMouseMove}
        onTouchMove={this.handleMouseMove}
        onMouseLeave={this.handleMouseLeave}
        ref={node => (this.videoContainer = node)}
        $thisStyles={thisStyles}
        $backgroundColor={backgroundColor}
      >

        {
          isStreamLive ? '' : (
            <VideoPlayerStreamOffline>
              Stream is offline. Please wait...
            </VideoPlayerStreamOffline>
          )
        }
        {!canPlay && !hideLoader? (
          <Loader
            thisStyles={{
              position: 'absolute',
              display: 'flex',
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: '1'
            }}
            thisStylesIcon={
              {
                height: 'auto'
              }
            }
          />
        ) : ''}


        {
          videoSources ? (
            <VideoPlayerHolder
              controls={false}
              isBackground={isBackground}
              isResponsive={isResponsive}
              muted={isBackground ? true : muted}
              autoPlay={isBackground}
              loop={loop}
              onLoadedMetadata={() => this.setDuration(analytics ? true : false)}
              onCanPlay={this.handleReadyStateChange}
              onCanPlayThrough={this.handleReadyStateChange}
              onStalled={(e) => this.handleReadyStateChange(e, 'stalled')}
              onSuspend={(e) => this.handleReadyStateChange(e, 'suspend')}
              onWaiting={this.handleReadyStateChange}
              onLoadedData={this.handleLoadedData}
              onTimeUpdate={this.handleTimeUpdate}
              onEnded={this.handleEnded}
              // onError={(e) => this.handleError(e, 'error')}
              // onAbort={(e) => this.handleError(e, 'abort')}
              ref={node => {
                this.video = node;
              }}
              poster={sources?.muxPlaybackId && !isBackground ? `https://image.mux.com/${sources?.muxPlaybackId}/thumbnail.jpg?fit_mode=smartcrop&time=${thumbnailTimestamp ? thumbnailTimestamp : 1}` : ''}
          >
            {videoSources}
          </VideoPlayerHolder>
          ) : (
            <VideoPlayerHolder
              controls={false}
              isBackground={isBackground}
              isResponsive={isResponsive}
              muted={isBackground ? true : muted}
              loop={loop}
              onLoadedMetadata={() => this.setDuration(analytics ? true : false)}
              onCanPlay={this.handleReadyStateChange}
              onCanPlayThrough={this.handleReadyStateChange}
              onStalled={(e) => this.handleReadyStateChange(e, 'stalled')}
              onSuspend={(e) => this.handleReadyStateChange(e, 'suspend')}
              onWaiting={this.handleReadyStateChange}
              onLoadedData={this.handleLoadedData}
              onTimeUpdate={this.handleTimeUpdate}
              onEnded={this.handleEnded}
              autoPlay={isBackground}
              // onError={(e) => this.handleError(e, 'error')}
              // onAbort={(e) => this.handleError(e, 'abort')}
              ref={node => {
                this.video = node;
              }}
              poster={sources?.muxPlaybackId && !isBackground ? `https://image.mux.com/${sources?.muxPlaybackId}/thumbnail.jpg?fit_mode=smartcrop&time=${thumbnailTimestamp ? thumbnailTimestamp : 1}` : ''}
          />
          )
        }

        {videoLoaded ? (
          <VideoPlayerControls
            controls={controls}
            currentTime={currentTime}
            isFullscreen={isFullscreen}
            duration={duration}
            isLive={isLive}
            onChangeProgress={this.handleChangeProgress}
            onFullscreenChange={this.toggleFullscreen}
            onPlay={this.play}
            onPause={this.pause}
            onVolumeChange={this.handleVolumeChange}
            paused={this.video.paused || paused}
            visible={isMouseOver}
            volume={muted ? 0 : this.video.volume}
            handlePictureInPicture={this.handlePictureInPicture}
            videoRef={this.video}
          />
        ) : (
          ''
        )}
        {videoAnalyticsData ? (
          <VideoObserver 
            videoRef={this.video}
            videoAnalyticsData={{
            duration: this.video ? duration : '',
            ...videoAnalyticsData
            }}
            videoViewId={this.state.videoViewId}
            hls={this.state.hlsVideo}
            Hls={this.state.Hls}
          />
        ) : ''}
      </VideoPlayerWrapper>
    );
  }
}

VideoPlayer.propTypes = {
  autoplay: PropTypes.bool,
  controls: PropTypes.bool,
  isBackground: PropTypes.bool,
  isLive: PropTypes.bool,
  isResponsive: PropTypes.bool,
  muted: PropTypes.bool,
  sources: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
};

export default VideoPlayer;
